import { Fragment, useEffect, useMemo, useState } from "react";

import type { MonitorAndProtectModelTrademarksTrademarkModel } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpTrademarkNavigation } from "../../../app/navigation";
import { useGetTrademarks } from "../../../services/api/trademarks";
import { useMonitorAndProtectTrademarkDataAndParams } from "../../hooks/trademark-params";
import { useSelectTrademark } from "../../hooks/use-select-trademark";
import { Favicon } from "../../ui/layout/favicon";

import type { ITrademarkSelectorData } from "./styled";
import { TrademarkEmptyState, TrademarkSelectedState, ComboboxStyled, MagicBackground } from "./styled";

interface ITrademarkOptionProps {
    trademark: MonitorAndProtectModelTrademarksTrademarkModel;
    selectedTrademarkId?: string;
    onTrademarkChange: (trademark: MonitorAndProtectModelTrademarksTrademarkModel) => void;
}

const TrademarkOption = ({ trademark, selectedTrademarkId, onTrademarkChange }: ITrademarkOptionProps) => {
    return (
        <Combobox.Option
            key={trademark.id}
            value={trademark}
            isSelected={selectedTrademarkId === trademark.id}
            onClick={() => onTrademarkChange(trademark)}
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <Favicon
                        iconUrl={getFaviconUrl(trademark.domain)}
                        alt={trademark.domain}
                        size={32}
                    />
                    <Combobox.OptionLabel color={colorsPalettes.carbon[500]}>{trademark.name}</Combobox.OptionLabel>
                </Flex>
            </Flex>
        </Combobox.Option>
    );
};

const fromListTrademark = (trademark: MonitorAndProtectModelTrademarksTrademarkModel): ITrademarkSelectorData => ({
    id: trademark.id,
    name: trademark.name,
    domain: trademark.domain,
});

export const TrademarkConfigDropdown = () => {
    const translate = useTranslation();
    const { data: trademarkList } = useGetTrademarks();
    const { trademarkId } = useMonitorAndProtectTrademarkDataAndParams();
    const { selectTrademark } = useSelectTrademark();
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const selectedTrademark = useMemo(() => {
        return trademarkId ? trademarkList?.find((trademark) => trademark.id === trademarkId) : null;
    }, [trademarkId, trademarkList]);

    const filteredTrademarks = useMemo(() => {
        return trademarkList?.filter((trademark) => {
            const lowerCaseSearchValue = searchValue.toLowerCase();
            return trademark.name.toLowerCase().includes(lowerCaseSearchValue);
        });
    }, [trademarkList, searchValue]);

    const shouldAutoOpenTrademarkSelect = !selectedTrademark && !isOpen;

    useEffect(() => {
        if (shouldAutoOpenTrademarkSelect) {
            setIsOpen(true);
        }
    }, [shouldAutoOpenTrademarkSelect]);

    const handleTrademarkChange = (trademark: MonitorAndProtectModelTrademarksTrademarkModel) => {
        selectTrademark(trademark);
        setIsOpen(false);
        setSearchValue("");
    };

    const handleRedirectToSettings = () => {
        void mnpTrademarkNavigation.trademarksEdit.go({
            trademarkId,
        });
    };

    return (
        <Fragment>
            <MagicBackground $active={isOpen} />
            <Flex
                alignItems="center"
                gap="8px"
            >
                <ComboboxStyled
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    placement="top-start"
                    offset={-40}
                >
                    <Combobox.Trigger>
                        <div>
                            {selectedTrademark ? (
                                <TrademarkSelectedState trademark={fromListTrademark(selectedTrademark)} />
                            ) : (
                                <TrademarkEmptyState />
                            )}
                        </div>
                    </Combobox.Trigger>

                    <Combobox.SearchInput
                        value={searchValue}
                        onChange={setSearchValue}
                        placeholder={translate("monitor_and_protect.component.trademark_select.placeholder")}
                    />

                    <Combobox.Content emptyState={translate("monitor_and_protect.component.trademark_select.no_data")}>
                        <Combobox.Section>
                            <Combobox.SectionHeader>
                                {translate("monitor_and_protect.component.trademark_select.header")}
                            </Combobox.SectionHeader>
                            {filteredTrademarks.map((trademark) => (
                                <TrademarkOption
                                    key={trademark.id}
                                    trademark={trademark}
                                    selectedTrademarkId={trademarkId}
                                    onTrademarkChange={handleTrademarkChange}
                                />
                            ))}
                        </Combobox.Section>
                    </Combobox.Content>
                </ComboboxStyled>
                <IconButton
                    type="flat"
                    iconName="settings"
                    iconSize="sm"
                    onClick={handleRedirectToSettings}
                />
            </Flex>
        </Fragment>
    );
};
